// Schedule page (card a1f0fff5): row labels, search, the board's state from
// the API and back, and the CSV export. Pure functions; the admin app
// carries a verbatim copy: keep the two in step.
import { sortCompetitions } from '../../../../helper/SortSyllabus';
import { ageLabel, danceName, hasAge, levelOf, matches, nice } from '../Tabulation/resultStatusModel';
import { estimate, flow, formatTime, isChampionship, parseTime } from './scheduleTiming';

export const DEFAULT_START = 8 * 60; // a new stage starts at 8:00 am
export const DEFAULT_BREAK = 30;

// ---- labels ----------------------------------------------------------------
// Line 1: the level. Line 2: dance and age, the Results Status wording with
// the age short ("U9", "15&O"). Line 3: dancers at a time.

export const levelLine = (c) => {
  const lv = levelOf(c?.group);
  if (lv === 'Prelim') return 'Prelim Championship';
  if (lv === 'Open') return 'Open Championship';
  if (lv) return lv;
  const g = String(c?.group || '');
  if (/TEAM/i.test(g)) return 'Teams';
  if (/SPECIAL/i.test(g)) return 'Special';
  return nice(g);
};

export const shortAge = (label) =>
  String(label || '')
    .replace(/^Under (\d+)$/, 'U$1')
    .replace(/^(\d+) and over$/, '$1&O');

export const danceLine = (c) => {
  const dance = danceName(c);
  const age = hasAge(c?.description || c?.name) ? '' : shortAge(ageLabel(c?.ages));
  return [dance === 'Championship' ? '' : dance, age].filter(Boolean).join(' · ') || String(c?.code || '');
};

export const atATimeLine = (c, est) => {
  const e = est || estimate(c);
  const ks = e.parts.map((p) => p.k);
  if (e.rounds > 1) return `${e.rounds} rounds · ${ks.join(', ')} at a time`;
  if (/TEAM/i.test(String(c?.group || '')) || /hand|ceili|céilí/i.test(`${c?.description} ${c?.name}`)) {
    return `${ks[0] || 1} team${ks[0] === 1 ? '' : 's'} at a time`;
  }
  return `${ks[0] || 2} at a time`;
};

// To schedule filters
export const kindOf = (c) => {
  if (isChampionship(c)) return 'champ';
  const lv = levelOf(c?.group);
  return lv && lv !== 'Prelim' && lv !== 'Open' ? 'grade' : 'special';
};
// One filter chip per level the event has, in syllabus order, each with how
// many of its competitions are still to schedule (Devin 2026-10-05: a bubble
// for every level). all: every competition of the event; pool: those not on a
// stage yet.
export const levelChips = (all, pool) => {
  const left = {};
  for (const c of pool || []) {
    const k = levelLine(c) || 'Other';
    left[k] = (left[k] || 0) + 1;
  }
  return groupPool(all).map((g) => ({ key: g.title, label: g.title, left: left[g.title] || 0 }));
};

// Search: the code, or every typed word in the name (Results Status rules);
// a break by its label.
export const itemMatches = (item, comp, query) => {
  if (!String(query || '').trim()) return true;
  if (item?.type === 'break') return String(item.label || 'break').toLowerCase().includes(String(query).trim().toLowerCase());
  return comp ? matches(comp, query) : String(item?.code || '').toLowerCase().includes(String(query).trim().toLowerCase());
};

// The pool in syllabus order, grouped by level line.
export const groupPool = (comps) => {
  const groups = [];
  for (const c of sortCompetitions([...(comps || [])])) {
    const title = levelLine(c) || 'Other';
    let g = groups[groups.length - 1];
    if (!g || g.title !== title) {
      g = groups.find((x) => x.title === title);
      if (!g) {
        g = { title, comps: [] };
        groups.push(g);
      }
    }
    g.comps.push(c);
  }
  return groups;
};

// ---- state -----------------------------------------------------------------

let seq = 0;
export const newId = (prefix) => {
  seq += 1;
  const rnd = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${seq}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rnd}`;
};

const lengthOf = (start, end) => {
  const s = parseTime(start);
  const e = parseTime(end);
  if (s == null || e == null) return DEFAULT_BREAK;
  const d = (e - s + 1440) % 1440;
  return d || DEFAULT_BREAK;
};

const cleanCounts = (list) =>
  Array.isArray(list) && list.length
    ? list
        .filter((d) => Number(d?.round) >= 1 && Number(d?.numberOfDancers) >= 1)
        .map((d) => ({ round: Number(d.round), numberOfDancers: Number(d.numberOfDancers) }))
    : undefined;

// competitions: GET /schedule-maker/list-competition (only those with
// dancers); stages: POST /schedule-maker/list-schedules (the draft).
// A timing whose competition was merged away or deleted, or has no dancers
// left, is kept on its stage and flagged: Save leaves it out.
export const buildBoard = (competitions, stages) => {
  const comps = {};
  for (const c of competitions || []) comps[String(c._id)] = c;
  const placed = new Set();
  const board = [...(stages || [])]
    .sort((a, b) => (Number(a.stage) || 0) - (Number(b.stage) || 0))
    .map((s, i) => {
      const timings = (s.timings || []).filter((t) => t && t.type);
      const items = timings.map((t) => {
        const fixedAt = parseTime(t.startTime);
        if (t.type === 'break') {
          const breakId = t.breakId || newId('break');
          return {
            key: breakId,
            type: 'break',
            breakId,
            label: String(t.label || '').trim() || 'Break',
            minutes: lengthOf(t.startTime, t.endTime),
            fixed: Boolean(t.fixed),
            fixedAt,
          };
        }
        const id = String(t.competitionId || t.competitionDetails?._id || '');
        const live = comps[id];
        const problem = !t.competitionDetails ? 'missing' : !live ? 'empty' : null;
        if (live) placed.add(id);
        return {
          key: `comp-${id || newId('x')}`,
          type: 'competition',
          competitionId: id,
          code: live?.code || t.competitionDetails?.code || t.code || '',
          detail: live || t.competitionDetails || null,
          problem,
          dancerCount: cleanCounts(t.dancerCount),
          fixed: Boolean(t.fixed),
          fixedAt,
        };
      });
      return {
        id: String(s._id),
        name: String(s.stageName || '').trim() || `Stage ${i + 1}`,
        // the stage's own start; stages saved before it was kept fall back to
        // their first row (web review R1-F1)
        startMin: parseTime(s.startTime) ?? parseTime(timings[0]?.startTime) ?? DEFAULT_START,
        items,
      };
    });
  const pool = (competitions || []).filter((c) => !placed.has(String(c._id)));
  return { comps, stages: board, pool };
};

// minutes of one item on the board
export const itemMinutes = (item, comps) => {
  if (item.type === 'break') return Math.max(0, Number(item.minutes) || 0);
  if (item.problem) return 0;
  const c = comps[item.competitionId];
  return c ? estimate(c, item.dancerCount).minutes : 0;
};

// A stage's times: one entry per item (see scheduleTiming.flow)
// A flagged (merged-away) row takes no time and its fixed start is ignored,
// so the times match what Save keeps (web review R1-F2).
export const stageTimes = (stage, comps) =>
  flow(
    stage.items.map((item) => (item.type === 'competition' && item.problem ? { ...item, fixed: false } : item)),
    stage.startMin,
    (item) => itemMinutes(item, comps)
  );

// POST /schedule-maker/update-schedule body
export const savePayload = (eventId, board, comps) => ({
  eventId,
  data: board.map((stage, i) => {
    const times = stageTimes(stage, comps);
    const timings = [];
    stage.items.forEach((item, j) => {
      if (item.type === 'competition' && item.problem) return; // merged away: dropped on save
      const t = times[j];
      const base = { startTime: formatTime(t.start), endTime: formatTime(t.end) };
      if (item.fixed || j === 0) base.fixed = Boolean(item.fixed);
      if (item.type === 'break') {
        timings.push({ type: 'break', breakId: item.breakId, label: String(item.label || '').trim().slice(0, 40), ...base });
      } else {
        const c = comps[item.competitionId];
        const counts = item.dancerCount || cleanCounts(c?.dancerCount);
        timings.push({
          type: 'competition',
          competitionId: item.competitionId,
          code: String(c?.code || item.code || '').slice(0, 40),
          ...base,
          ...(counts ? { dancerCount: counts } : {}),
        });
      }
    });
    return {
      _id: stage.id,
      stage: i + 1,
      stageName: String(stage.name || '').trim().slice(0, 60) || `Stage ${i + 1}`,
      startTime: formatTime(Number.isFinite(stage.startMin) ? stage.startMin : DEFAULT_START),
      timings,
    };
  }),
});

// ---- CSV -------------------------------------------------------------------

const cell = (v) => {
  let s = String(v ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`; // never a spreadsheet formula
  return `"${s.replace(/"/g, '""')}"`;
};

export const CSV_HEADER = ['Stage', 'Start', 'End', 'Minutes', 'Code', 'Level', 'Competition', 'Dancers'];

export const csvRows = (board, comps) => {
  const rows = [CSV_HEADER];
  for (const stage of board) {
    const times = stageTimes(stage, comps);
    stage.items.forEach((item, j) => {
      if (item.type === 'competition' && item.problem) return;
      const t = times[j];
      const when = [formatTime(t.start), formatTime(t.end), Math.round(t.minutes)];
      if (item.type === 'break') {
        rows.push([stage.name, ...when, '', 'Break', item.label || 'Break', '']);
      } else {
        const c = comps[item.competitionId];
        rows.push([stage.name, ...when, c?.code || item.code, levelLine(c), danceLine(c), (c?.dancers || []).length]);
      }
    });
  }
  return rows;
};

export const toCsv = (rows) => `${rows.map((r) => r.map(cell).join(',')).join('\r\n')}\r\n`;
